import { LegalPage, Section, SubSection } from "./LegalPage";

export function Terms() {
  return (
    <LegalPage title="Terms of Service" updatedOn="April 23, 2026">
      <p>
        These Terms of Service ("<strong>Terms</strong>") govern your use of
        Proofly (the "<strong>Service</strong>"), operated by Proofly, Inc. (
        "<strong>Proofly</strong>," "<strong>we</strong>," "
        <strong>us</strong>"). By creating an account or using the Service,
        you agree to these Terms. If you are using the Service on behalf of an
        organization, you represent that you have authority to bind that
        organization and "<strong>you</strong>" refers to that organization.
      </p>

      <Section title="1. The Service">
        <p>
          Proofly helps small and medium businesses keep financial records
          complete and audit-ready — including receipt and invoice capture,
          categorization, policies, approvals, reconciliation, and export. The
          Service is not tax, legal, or accounting advice and is not a
          substitute for a qualified professional.
        </p>
      </Section>

      <Section title="2. Accounts">
        <SubSection title="2.1 Eligibility">
          You must be at least 18 years old and able to form a binding contract.
        </SubSection>
        <SubSection title="2.2 Registration">
          Provide accurate, current information. You're responsible for
          safeguarding your credentials and for activity under your account.
        </SubSection>
        <SubSection title="2.3 Free plan">
          The Free plan is offered for organizations with fewer than 10 users.
          It is free forever within those limits — there is no trial clock and
          no feature-downgrade after onboarding. If your organization exceeds 9
          users, you will be prompted to upgrade within 14 days to keep adding
          seats.
        </SubSection>
      </Section>

      <Section title="3. Fees & billing">
        <SubSection title="3.1 Paid plans">
          Fees for Team and Enterprise plans are listed on{" "}
          <a href="/pricing">proofly.one/pricing</a> (or in your order form,
          for Enterprise). Fees exclude taxes, which you're responsible for
          unless we're required to collect them.
        </SubSection>
        <SubSection title="3.2 Annual vs monthly">
          Annual subscriptions are billed upfront at the annual rate. Monthly
          subscriptions renew every 30 days. You may cancel renewal anytime
          from Settings.
        </SubSection>
        <SubSection title="3.3 Refunds">
          Except where required by law, fees are non-refundable. We will refund
          the unused portion of an annual subscription if we materially fail
          to deliver the Service for more than 72 consecutive hours.
        </SubSection>
      </Section>

      <Section title="4. Your content & data">
        <SubSection title="4.1 Your data is yours">
          Documents, transactions, categories, policies, and any other material
          you upload ("<strong>Customer Data</strong>") remain yours. You grant
          us a limited license to host, process, transmit, and display
          Customer Data as necessary to provide the Service and to improve it
          in aggregated, de-identified form.
        </SubSection>
        <SubSection title="4.2 Export">
          You can export Customer Data as PDF, CSV, or JSON at any time from
          the app — on every plan. On account termination, export access
          remains available for 30 days.
        </SubSection>
        <SubSection title="4.3 AI features">
          Some features use AI models to extract fields, suggest categories,
          and flag anomalies. These are suggestions, not decisions — you
          remain responsible for reviewing and accepting them. We do not train
          foundation models on your private Customer Data.
        </SubSection>
      </Section>

      <Section title="5. Acceptable use">
        <p>You won't, and won't permit any third party to:</p>
        <ul>
          <li>Use the Service to violate applicable law;</li>
          <li>Upload malware, infringing content, or content you don't have the right to share;</li>
          <li>Reverse engineer, scrape, or interfere with Service operations beyond rate limits we publish;</li>
          <li>Use the Service to send spam, phishing, or deceptive messages;</li>
          <li>Resell, sublicense, or otherwise offer the Service as your own without written permission.</li>
        </ul>
      </Section>

      <Section title="6. Third-party integrations">
        <p>
          The Service connects to third-party platforms (for example,
          QuickBooks, Xero, Stripe, Plaid, Gmail) at your direction. Those
          third parties have their own terms; we're not responsible for their
          actions or availability. If a third party disconnects or changes its
          API, we'll do our best to preserve your records — but we don't
          guarantee uninterrupted integration.
        </p>
      </Section>

      <Section title="7. Compliance disclaimers">
        <p>
          Proofly helps you stay audit-ready; it does not certify tax positions
          or guarantee regulatory outcomes. You're responsible for filing
          returns, paying taxes, and complying with applicable law. Consult a
          qualified accountant or attorney for advice specific to your
          situation.
        </p>
      </Section>

      <Section title="8. Confidentiality">
        <p>
          Each party will keep the other's non-public information confidential
          using the care it uses for its own confidential information (and at
          least reasonable care), and use it only to perform under these
          Terms.
        </p>
      </Section>

      <Section title="9. Intellectual property">
        <p>
          We own the Service, including its software, design, and brand. These
          Terms don't grant you any right in our IP other than the right to
          use the Service as described.
        </p>
      </Section>

      <Section title="10. Warranty disclaimer">
        <p>
          The Service is provided "as is" and "as available." We disclaim all
          warranties, express or implied, to the fullest extent permitted by
          law, including merchantability, fitness for a particular purpose,
          and non-infringement.
        </p>
      </Section>

      <Section title="11. Limitation of liability">
        <p>
          To the maximum extent permitted by law, neither party will be liable
          for indirect, incidental, consequential, or punitive damages. Our
          total liability arising out of or relating to these Terms will not
          exceed the amount you paid to Proofly in the 12 months preceding the
          event giving rise to the claim (or USD $100 if you're on the Free
          plan).
        </p>
      </Section>

      <Section title="12. Indemnification">
        <p>
          You agree to defend and indemnify Proofly against any claim by a
          third party arising from your Customer Data or your breach of these
          Terms, excluding claims for which we are responsible under
          Section 11.
        </p>
      </Section>

      <Section title="13. Term & termination">
        <p>
          These Terms apply while you use the Service. Either party may
          terminate for material breach not cured within 30 days of written
          notice. On termination, your access ends, we'll retain Customer
          Data for 30 days to let you export, and then delete it on a
          schedule described in our{" "}
          <a href="/privacy">Privacy Policy</a>.
        </p>
      </Section>

      <Section title="14. Changes">
        <p>
          We may update these Terms from time to time. If changes are
          material, we'll post notice at least 30 days before they take
          effect. Continued use after the effective date means you accept the
          changes.
        </p>
      </Section>

      <Section title="15. Governing law & disputes">
        <p>
          These Terms are governed by the laws of the State of Delaware,
          without regard to conflict-of-laws rules. Disputes will be resolved
          in the state or federal courts located in Delaware, and each party
          consents to venue there. Nothing here prevents either party from
          seeking injunctive relief in any court of competent jurisdiction.
        </p>
      </Section>

      <Section title="16. Contact">
        <p>
          Questions about these Terms?{" "}
          <a href="mailto:legal@proofly.one">legal@proofly.one</a>.
        </p>
      </Section>
    </LegalPage>
  );
}
